import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import StopIcon from '@mui/icons-material/Stop';
import { VOICE_FALLBACK_LANGUAGES } from '@ast-data/shared';
import { compareNatural } from '../../utils/compare';
import { defineStoredString } from '../../utils/storage';
import { useTranslationAvailability } from './translationApi';
import { fetchVoiceLanguages, type VoiceLanguage } from './voiceApi';
import { transcriptText } from './voiceTranscript';
import { useVoiceDictation, type VoiceResult } from './useVoiceDictation';

/** "Let DeepL tell" — the default, and an entry of the language list. */
const AUTO = 'auto';

/** The language last chosen, so the next recording starts with it. */
const storedLanguage = defineStoredString<VoiceLanguage>({
    key: 'voice_language'
});

/** Whether the last recording was translated — the next one asks the same. */
const storedTranslate = defineStoredString<'yes' | 'no'>({
    key: 'voice_translate',
    allow: ['yes', 'no']
});

/**
 * DeepL's list, asked for when the panel first opens; German and English
 * until it is there. The language chosen last time is always among them.
 * Ours first, then the rest by their names in the interface language.
 */
function useLanguageOptions(
    wanted: boolean,
    current: VoiceLanguage
): { code: VoiceLanguage; name: string }[] {
    const intl = useIntl();
    const [codes, setCodes] = useState<readonly VoiceLanguage[]>(
        VOICE_FALLBACK_LANGUAGES
    );
    useEffect(() => {
        if (!wanted) {
            return;
        }
        let active = true;
        void fetchVoiceLanguages().then((languages) => {
            if (active) {
                setCodes(languages);
            }
        });
        return () => {
            active = false;
        };
    }, [wanted]);
    return useMemo(() => {
        const names = new Intl.DisplayNames([intl.locale], {
            type: 'language'
        });
        const nameOf = (code: VoiceLanguage) => names.of(code) ?? code;
        const first: VoiceLanguage[] = ['de', 'en'];
        const all =
            current === AUTO || codes.includes(current)
                ? codes
                : [...codes, current];
        const rest = all
            .filter((code) => !first.includes(code))
            .map((code) => ({ code, name: nameOf(code) }))
            .sort((leftName, rightName) =>
                compareNatural(leftName.name, rightName.name)
            );
        return [
            { code: AUTO, name: intl.formatMessage({ id: 'voice.auto' }) },
            ...first
                .filter((code) => all.includes(code))
                .map((code) => ({ code, name: nameOf(code) })),
            ...rest
        ];
    }, [intl, codes, current]);
}

/**
 * Dictation through DeepL Voice, next to the browser's own: for languages the
 * browser does not recognise well, and to have what is said translated into
 * German straight away.
 *
 * `canTranslate` — the field keeps translations. The German then goes in as
 * the field's translation, never as its text: it is machine output, and only
 * an accepted translation reaches a deliverable.
 */
export function VoiceDictationButton({
    disabled,
    canTranslate,
    projectId,
    onStart,
    onLive,
    onResult,
    onEnd
}: {
    disabled?: boolean;
    canTranslate: boolean;
    projectId?: string;
    /** The recording is about to start: the field takes its anchor. */
    onStart: () => void;
    /** What DeepL has heard so far, its last guesses included. */
    onLive: (text: string) => void;
    /** The final text (and its German, when translated). */
    onResult: (result: VoiceResult) => void;
    /** The recording is over — after `onResult`, or without one. */
    onEnd: () => void;
}) {
    const intl = useIntl();
    const { voice, online } = useTranslationAvailability();
    const dictation = useVoiceDictation();
    /**
     * The field's CURRENT handlers: the text arrives while and after the
     * person speaks, and must meet the field as it is then, not as it was
     * when the recording started.
     */
    const handlersRef = useRef({ onStart, onLive, onResult, onEnd });
    useEffect(() => {
        handlersRef.current = { onStart, onLive, onResult, onEnd };
    });
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [language, setLanguage] = useState<VoiceLanguage>(
        () => storedLanguage.read() ?? AUTO
    );
    const languages = useLanguageOptions(anchor !== null, language);
    const [translate, setTranslate] = useState(
        () => storedTranslate.read() !== 'no'
    );

    const { state, transcript, error } = dictation;
    const active = state !== 'idle';
    const liveText = active ? transcriptText(transcript.source, true) : '';
    useEffect(() => {
        if (active) {
            handlersRef.current.onLive(liveText);
        }
    }, [active, liveText]);
    const wasActive = useRef(false);
    useEffect(() => {
        if (wasActive.current && !active) {
            handlersRef.current.onEnd();
        }
        wasActive.current = active;
    }, [active]);
    // Taken away mid-recording (the form closed, the field went read-only):
    // the hook drops the session, and the field must be handed back too.
    useEffect(
        () => () => {
            if (wasActive.current) {
                handlersRef.current.onEnd();
            }
        },
        []
    );
    // A failure shows where the recording was set up.
    useEffect(() => {
        if (error && buttonRef.current) {
            setAnchor(buttonRef.current);
        }
    }, [error]);

    if (!voice) {
        return null;
    }

    const translating = canTranslate && translate && language !== 'de';

    function startRecording() {
        storedLanguage.write(language);
        storedTranslate.write(translate ? 'yes' : 'no');
        // The words go into the field as they are spoken; the panel is done.
        setAnchor(null);
        handlersRef.current.onStart();
        void dictation.start(
            {
                sourceLang: language === AUTO ? null : language,
                translate: translating,
                projectId
            },
            (result) => handlersRef.current.onResult(result)
        );
    }

    const title = intl.formatMessage({
        id:
            state === 'connecting'
                ? 'voice.state.connecting'
                : active
                  ? 'voice.stop'
                  : online
                    ? 'voice.open'
                    : 'voice.offline'
    });

    return (
        <>
            {/* A span, so the tooltip still explains a disabled button. */}
            <Tooltip title={title}>
                <span>
                    <IconButton
                        ref={buttonRef}
                        size="small"
                        color={active ? 'error' : 'default'}
                        disabled={
                            disabled ||
                            state === 'finishing' ||
                            (!active && !online)
                        }
                        aria-label={title}
                        onClick={(event) =>
                            active
                                ? dictation.stop()
                                : setAnchor(event.currentTarget)
                        }
                    >
                        {/* Connecting: wait, DeepL is not listening yet. */}
                        {state === 'connecting' || state === 'finishing' ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : active ? (
                            <StopIcon fontSize="small" />
                        ) : (
                            <RecordVoiceOverIcon fontSize="small" />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
            <Popover
                open={anchor !== null}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ p: 2, width: 340, maxWidth: '100vw' }}>
                    <Stack spacing={1.5}>
                        {/* Forty-odd languages: typing "pol" beats
                            scrolling. Never empty — clearing it would
                            leave no language and no detection. */}
                        <Autocomplete
                            size="small"
                            disableClearable
                            options={languages}
                            value={
                                languages.find(
                                    (option) => option.code === language
                                ) ?? languages[0]
                            }
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) =>
                                option.code === value.code
                            }
                            onChange={(_event, option) =>
                                setLanguage(option.code)
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={intl.formatMessage({
                                        id: 'voice.language'
                                    })}
                                />
                            )}
                        />
                        {canTranslate && language !== 'de' && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={translate}
                                        onChange={(event) =>
                                            setTranslate(event.target.checked)
                                        }
                                    />
                                }
                                label={intl.formatMessage({
                                    id: 'voice.translate'
                                })}
                            />
                        )}
                        {translating && (
                            <Typography variant="body2" color="text.secondary">
                                {intl.formatMessage({
                                    id: 'voice.translateHint'
                                })}
                            </Typography>
                        )}
                        {error && (
                            <Alert severity="warning">
                                {intl.formatMessage({
                                    id: `voice.error.${error}`
                                })}
                            </Alert>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<RecordVoiceOverIcon />}
                            disabled={!online}
                            onClick={startRecording}
                        >
                            {intl.formatMessage({ id: 'voice.start' })}
                        </Button>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
}
